import type { OptimisationCompareDto } from '@cutlyft/zod-schemas';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { RunStatusBadge } from '@/pages/projects/components/run-status-badge';

interface RunComparisonProps {
  comparison: OptimisationCompareDto;
}

function Stat({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="text-sm">
      <p className="text-fg-muted text-xs">{label}</p>
      <p className="font-semibold">{value}</p>
    </div>
  );
}

function mmStr(mm: number | null) {
  if (mm == null) return '—';
  return mm >= 1000 ? `${(mm / 1000).toFixed(2)} m` : `${mm} mm`;
}

export function RunComparison({ comparison }: RunComparisonProps) {
  const { runA, runB, winner, wasteDiffMm, barsDiff } = comparison;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <h3 className="text-sm font-semibold">Comparison Result</h3>
        <Badge variant={winner === 'TIE' ? 'secondary' : 'default'}>
          {winner === 'TIE' ? 'Tie' : `Run ${winner} wins`}
        </Badge>
      </div>

      {(wasteDiffMm != null || barsDiff != null) && (
        <div className="grid grid-cols-2 gap-3 text-sm">
          {wasteDiffMm != null && (
            <Stat
              label="Waste difference (A − B)"
              value={
                <span className={wasteDiffMm < 0 ? 'text-green-600' : wasteDiffMm > 0 ? 'text-destructive' : undefined}>
                  {wasteDiffMm > 0 ? '+' : ''}{mmStr(wasteDiffMm)}
                </span>
              }
            />
          )}
          {barsDiff != null && (
            <Stat
              label="Bars difference (A − B)"
              value={
                <span className={barsDiff < 0 ? 'text-green-600' : barsDiff > 0 ? 'text-destructive' : undefined}>
                  {barsDiff > 0 ? '+' : ''}{barsDiff}
                </span>
              }
            />
          )}
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-2">
        {([['A', runA], ['B', runB]] as const).map(([label, run]) => (
          <Card key={label} className={winner === label ? 'ring-2 ring-primary' : undefined}>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm">Run {label} — #{run.id}</CardTitle>
                <RunStatusBadge status={run.runStatus} />
              </div>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-2">
              <Stat label="Bars" value={run.totalBarsUsed ?? '—'} />
              <Stat label="Waste" value={mmStr(run.totalWasteMm)} />
              <Stat label="Algorithm" value={run.algorithmUsed ?? '—'} />
              <Stat label="Duration" value={run.durationMs != null ? `${run.durationMs} ms` : '—'} />
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
